import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, MapPin, X } from "lucide-react";

const DEFAULT_CENTER = [-7.5694, 110.8203]; // Banjarsari, Solo

export default function MapPicker({ value, onChange }) {
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const divRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const [locating, setLocating] = useState(false);

    const setMarker = (lat, lng) => {
        const map = mapRef.current;
        if (!map) return;
        if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
        } else {
            const icon = L.divIcon({
                className: "",
                html: `<div style="width:22px;height:22px;background:#3666E0;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
                iconSize: [22, 22],
                iconAnchor: [11, 11],
            });
            markerRef.current = L.marker([lat, lng], { icon, draggable: true }).addTo(map);
            markerRef.current.on("dragend", (e) => {
                const p = e.target.getLatLng();
                onChangeRef.current({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6) });
            });
        }
    };

    useEffect(() => {
        const map = L.map(divRef.current, {
            center: DEFAULT_CENTER,
            zoom: 13,
            scrollWheelZoom: false,
        });
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);
        map.on("click", (e) => {
            onChangeRef.current({ lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) });
            setMarker(e.latlng.lat, e.latlng.lng);
        });
        mapRef.current = map;
        return () => {
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (value) {
            setMarker(value.lat, value.lng);
        } else if (markerRef.current && mapRef.current) {
            mapRef.current.removeLayer(markerRef.current);
            markerRef.current = null;
        }
    }, [value]);

    const useMyLocation = () => {
        if (!navigator.geolocation) return;
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setMarker(latitude, longitude);
                mapRef.current?.setView([latitude, longitude], 16);
                onChangeRef.current({ lat: +latitude.toFixed(6), lng: +longitude.toFixed(6) });
                setLocating(false);
            },
            () => setLocating(false),
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    return (
        <div className="space-y-2.5">
            <div
                ref={divRef}
                data-testid="map-picker"
                className="h-56 w-full overflow-hidden rounded-xl border border-ink/10 z-0"
            />
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    data-testid="map-use-my-location"
                    onClick={useMyLocation}
                    disabled={locating}
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-mist px-3.5 py-1.5 text-xs font-bold text-brand transition-colors duration-200 hover:bg-brand hover:text-white disabled:opacity-60"
                >
                    <Crosshair size={13} />
                    {locating ? "Mencari lokasi..." : "Gunakan Lokasi Saya"}
                </button>
                {value && (
                    <button
                        type="button"
                        data-testid="map-clear-point"
                        onClick={() => onChange(null)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3.5 py-1.5 text-xs font-bold text-red-600 transition-colors duration-200 hover:bg-red-100"
                    >
                        <X size={13} />
                        Hapus Titik
                    </button>
                )}
                {value ? (
                    <span
                        data-testid="map-point-label"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-ink/50"
                    >
                        <MapPin size={12} className="text-brand" />
                        Titik terpasang: {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
                    </span>
                ) : (
                    <span className="text-xs text-ink/40">
                        Ketuk peta untuk menandai titik penjemputan
                    </span>
                )}
            </div>
        </div>
    );
}
